import React, { useRef, useState, useEffect } from "react";
import { Box, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
// Auto-truncating text cell with tooltip
const TruncatedCell = ({ text }) => {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);
    useEffect(() => {
        const el = textRef.current;
        if (el) {
            setIsTruncated(el.scrollWidth > el.clientWidth);
        }
    }, [text]);
    return (React.createElement(Tooltip, { title: isTruncated ? text : "", placement: "top" },
        React.createElement(Typography, { ref: textRef, variant: "body2", noWrap: true, sx: {
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
            } }, text)));
};
// Render a single cell using column definition
const renderCell = (column, row) => {
    if (column.renderCell) {
        return column.renderCell({
            row,
            value: row[column.field],
            field: column.field,
            id: row.id,
        });
    }
    const value = row[column.field];
    const text = value != null ? String(value) : "";
    return React.createElement(TruncatedCell, { text: text });
};
// Skeleton row for loading state — matches CardRow card style
export const SkeletonRow = ({ columns, computedWidthByField }) => (React.createElement(Box, { sx: {
        display: "flex",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 1,
        width: "100%",
        px: 2,
        py: 1.5,
        my: 1,
        backgroundColor: "background.paper",
        borderRadius: 3,
        border: "1px solid #919EAB1F",
    } }, columns.map((col, index) => {
    var _a, _b, _c;
    return (React.createElement(Box, { key: col.field, sx: Object.assign(Object.assign({}, ((computedWidthByField === null || computedWidthByField === void 0 ? void 0 : computedWidthByField[col.field]) != null
            ? {
                width: `${computedWidthByField[col.field]}px`,
                flex: "0 0 auto",
            }
            : {
                flexGrow: (_a = col.flex) !== null && _a !== void 0 ? _a : 0,
                flexShrink: 1,
                flexBasis: 0,
            })), { minWidth: (_c = ((_b = col.minWidth) !== null && _b !== void 0 ? _b : col.width)) !== null && _c !== void 0 ? _c : 100, maxWidth: col.maxWidth, display: "flex", flexDirection: "column", gap: 0.5 }) },
        React.createElement(Skeleton, { variant: "rounded", width: index % 3 === 0 ? "90%" : index % 3 === 1 ? "70%" : "80%", height: 16, sx: { borderRadius: 1 } }),
        col.flex && col.flex > 1 && (React.createElement(Skeleton, { variant: "rounded", width: "50%", height: 12, sx: { borderRadius: 1 } }))));
})));
// Card row component
export const CardRow = ({ row, columns, onClick, isMobile, computedWidthByField }) => {
    const handleClick = (e) => {
        if (onClick) {
            onClick({ row, id: row.id }, e);
        }
    };
    if (isMobile) {
        // On mobile, split columns: first column as icon, then stack the rest
        const firstCol = columns[0];
        const restCols = columns.slice(1);
        return (React.createElement(Box, { onClick: handleClick, sx: {
                p: 2, pr: 0,
                my: 1,
                backgroundColor: "background.paper",
                borderRadius: 3,
                border: "1px solid #919EAB1F",
                cursor: onClick ? "pointer" : "default",
                "&:hover": onClick ? { backgroundColor: "action.hover" } : undefined,
            } },
            React.createElement(Stack, { direction: "row", spacing: 2, alignItems: "flex-start" },
                firstCol && (React.createElement(Box, { "data-field": firstCol.field, sx: { flexShrink: 0 } }, renderCell(firstCol, row))),
                React.createElement(Box, { sx: { flex: 1, minWidth: 0 } },
                    React.createElement(Stack, { spacing: 0.75 }, restCols.map((col) => (React.createElement(Box, { key: col.field, "data-field": col.field }, renderCell(col, row)))))))));
    }
    return (React.createElement(Box, { onClick: handleClick, sx: {
            display: "flex",
            alignItems: "center",
            flexWrap: "nowrap",
            gap: 1,
            width: "100%",
            px: 2,
            py: 1.5,
            my: 1,
            backgroundColor: "background.paper",
            borderRadius: 3,
            border: "1px solid #919EAB1F",
            cursor: onClick ? "pointer" : "default",
        } }, columns.map((col) => {
        var _a, _b, _c;
        const minW = (_b = (_a = col.minWidth) !== null && _a !== void 0 ? _a : col.width) !== null && _b !== void 0 ? _b : 100;
        const computedW = computedWidthByField === null || computedWidthByField === void 0 ? void 0 : computedWidthByField[col.field];
        const flexGrow = (_c = col.flex) !== null && _c !== void 0 ? _c : 0;
        return (React.createElement(Box, { key: col.field, "data-field": col.field, sx: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (computedW != null
                ? {
                    width: `${computedW}px`,
                    flex: "0 0 auto",
                }
                : {
                    flexGrow,
                    // Fixed-width columns (e.g. actions) must not shrink or controls vanish after row updates.
                    flexShrink: flexGrow > 0 ? 1 : 0,
                    flexBasis: 0,
                })), { minWidth: minW, maxWidth: col.maxWidth, display: "flex", alignItems: "center" }), (col.align === "center" && { justifyContent: "center" })), (col.align === "right" && { justifyContent: "flex-end" })), { overflow: col.renderCell ? "visible" : "hidden" }) }, renderCell(col, row)));
    })));
};
